import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Rating,
  Divider,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Button,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import ProductReviewForm from './ProductReviewForm';
import { 
  db,
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  runTransaction
} from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';

// 样式化组件
const ReviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: theme.palette.divider,
  boxShadow: 'none',
}));

const ReviewAvatar = styled('div')(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: '1.25rem',
  marginRight: theme.spacing(2),
}));

const ReviewImage = styled('img')({
  width: '80px',
  height: '80px',
  objectFit: 'cover',
  borderRadius: '4px',
  cursor: 'pointer',
  margin: '0 8px 8px 0',
});

const RatingBar = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.primary.main,
  },
}));

// 评论组件
const ProductReviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState('latest');
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    ratingDistribution: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  // 获取当前用户信息
  const { currentUser } = useAuth();

  // 加载评价 - 直接从Firestore获取
  const loadReviews = async () => {
    if (!productId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // 构建查询
      let reviewsQuery = query(
        collection(db, 'reviews'),
        where('productId', '==', productId),
        where('status', '==', 'published')
      );
      
      // 根据排序方式设置排序
      switch (sort) {
        case 'rating_high':
          reviewsQuery = query(reviewsQuery, orderBy('rating', 'desc'));
          break;
        case 'rating_low':
          reviewsQuery = query(reviewsQuery, orderBy('rating', 'asc'));
          break;
        case 'latest':
        default:
          reviewsQuery = query(reviewsQuery, orderBy('createdAt', 'desc'));
      }
      
      // 获取总数
      const totalQuery = query(
        collection(db, 'reviews'),
        where('productId', '==', productId),
        where('status', '==', 'published')
      );
      const totalSnapshot = await getDocs(totalQuery);
      const total = totalSnapshot.size;
      
      // 计算分页
      const itemsPerPage = 5;
      const calculatedTotalPages = Math.ceil(total / itemsPerPage);
      setTotalPages(calculatedTotalPages);
      
      // 应用分页 - Firestore没有直接的offset分页，这里使用一个简单方法
      // 注意：大数据集可能需要使用游标分页
      const pageLimit = itemsPerPage;
      reviewsQuery = query(reviewsQuery, limit(page * pageLimit));
      
      // 执行查询
      const querySnapshot = await getDocs(reviewsQuery);
      
      // 取最后一页数据
      const allReviews = [];
      for (const doc of querySnapshot.docs) {
        allReviews.push({
          id: doc.id,
          ...doc.data()
        });
      }
      
      // 提取当前页数据
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const pagedReviews = allReviews.slice(startIndex, endIndex);
      
      // 获取每个评价的用户信息
      const reviewsWithUserInfo = await Promise.all(
        pagedReviews.map(async (review) => {
          try {
            // 获取用户信息
            const userDoc = await getDoc(doc(db, 'users', review.userId));
            const userData = userDoc.exists() ? userDoc.data() : null;
            
            return {
              ...review,
              userName: userData ? userData.displayName : '匿名用户',
              userAvatar: userData ? userData.photoURL : null,
              createdAt: review.createdAt ? review.createdAt.toDate().toISOString() : new Date().toISOString()
            };
          } catch (e) {
            console.error('无法获取用户信息:', e);
            return {
              ...review,
              userName: '匿名用户',
              userAvatar: null,
              createdAt: review.createdAt ? review.createdAt.toDate().toISOString() : new Date().toISOString()
            };
          }
        })
      );
      
      setReviews(reviewsWithUserInfo);
    } catch (error) {
      console.error('加载评价出错:', error);
      setError('获取评价数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 加载评价统计信息 - 直接从Firestore计算
  const loadStats = async () => {
    if (!productId) return;
    
    setStatsLoading(true);
    setStatsError('');
    
    try {
      // 检查是否有缓存的统计结果
      const statsDoc = await getDoc(doc(db, 'productStats', productId));
      
      if (statsDoc.exists() && statsDoc.data().lastUpdated) {
        const statsData = statsDoc.data();
        // 如果统计结果不超过1小时，使用缓存
        const lastUpdated = statsData.lastUpdated.toDate();
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        
        if (lastUpdated > oneHourAgo) {
          console.log('使用缓存的评价统计数据');
          setStats(statsData);
          setStatsLoading(false);
          return;
        }
      }
      
      // 需要重新计算统计数据
      console.log('计算新的评价统计数据');
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('productId', '==', productId),
        where('status', '==', 'published')
      );
      
      const snapshot = await getDocs(reviewsQuery);
      
      // 初始化统计数据
      const statsData = {
        totalReviews: snapshot.size,
        averageRating: 0,
        ratingDistribution: {
          1: 0, 2: 0, 3: 0, 4: 0, 5: 0
        },
        lastUpdated: serverTimestamp()
      };
      
      // 计算评分分布和平均分
      let totalRating = 0;
      snapshot.forEach(doc => {
        const rating = doc.data().rating;
        statsData.ratingDistribution[rating] = (statsData.ratingDistribution[rating] || 0) + 1;
        totalRating += rating;
      });
      
      // 计算平均分
      statsData.averageRating = statsData.totalReviews > 0 ? totalRating / statsData.totalReviews : 0;
      
      // 只有当用户已登录时才尝试更新统计缓存
      if (currentUser) {
        try {
          // 保存统计结果到缓存
          await setDoc(doc(db, 'productStats', productId), statsData, { merge: true });
          console.log('已更新评价统计缓存');
        } catch (cacheError) {
          console.warn('无法更新统计缓存，可能是权限问题:', cacheError);
          // 继续处理，不阻止显示统计结果
        }
      } else {
        console.log('用户未登录，跳过更新统计缓存');
      }
      
      setStats(statsData);
    } catch (error) {
      console.error('加载评价统计出错:', error);
      setStatsError('无法加载评价统计信息');
    } finally {
      setStatsLoading(false);
    }
  };

  // 首次加载和筛选条件变化时重新加载评价
  useEffect(() => {
    if (productId) {
      loadReviews();
      loadStats();
    }
  }, [productId, page, sort]);

  // 处理评价提交 - 直接使用Firestore事务
  const handleReviewSubmit = async (reviewData) => {
    try {
      // 验证用户身份
      if (!currentUser) {
        throw new Error('请先登录再评价');
      }
      
      const userId = currentUser.uid;
      
      // 验证参数
      if (!productId || !reviewData.rating) {
        throw new Error('产品ID和评分不能为空');
      }
      
      // 检查用户是否已评价过该产品
      const existingReviewQuery = query(
        collection(db, 'reviews'),
        where('userId', '==', userId),
        where('productId', '==', productId),
        limit(1)
      );
      
      const existingReviewSnapshot = await getDocs(existingReviewQuery);
      if (!existingReviewSnapshot.empty) {
        throw new Error('您已经评价过该商品');
      }
      
      // 检查用户是否购买过该产品
      const ordersQuery = query(
        collection(db, 'orders'),
        where('userId', '==', userId),
        where('status', '==', 'completed')
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      
      // 验证用户订单中是否包含该产品
      let hasPurchased = false;
      ordersSnapshot.forEach(orderDoc => {
        const orderData = orderDoc.data();
        if (orderData.items && orderData.items.some(item => item.productId === productId)) {
          hasPurchased = true;
        }
      });
      
      if (!hasPurchased) {
        throw new Error('只有购买过的用户才能评价');
      }
      
      // 使用事务更新产品评分和添加评价
      await runTransaction(db, async (transaction) => {
        // 首先完成所有读取操作
        
        // 1. 创建评价的准备工作
        const reviewRef = doc(collection(db, 'reviews'));
        
        // 获取用户信息
        const userDoc = await transaction.get(doc(db, 'users', userId));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        // 2. 读取产品信息
        const productRef = doc(db, 'products', productId);
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('产品不存在');
        }
        
        // 3. 读取统计缓存信息
        const statsRef = doc(db, 'productStats', productId);
        const statsDoc = await transaction.get(statsRef);
        
        // 准备评价数据
        const newReviewData = {
          userId,
          productId,
          rating: reviewData.rating,
          content: reviewData.content || '',
          images: reviewData.images || [],
          status: 'published',
          userName: userData.displayName || '匿名用户',
          userAvatar: userData.photoURL || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        // 准备产品数据
        const productData = productDoc.data();
        const totalRating = (productData.totalRating || 0) + reviewData.rating;
        const reviewCount = (productData.reviewCount || 0) + 1;
        const averageRating = totalRating / reviewCount;
        
        // 现在开始所有写入操作
        
        // 1. 创建评价
        transaction.set(reviewRef, newReviewData);
        
        // 2. 更新产品评分
        transaction.update(productRef, {
          totalRating,
          reviewCount,
          averageRating,
          updatedAt: serverTimestamp()
        });
        
        // 3. 更新产品统计缓存
        if (statsDoc.exists()) {
          const statsData = statsDoc.data();
          const ratingDistribution = { ...statsData.ratingDistribution };
          ratingDistribution[reviewData.rating] = (ratingDistribution[reviewData.rating] || 0) + 1;
          
          transaction.update(statsRef, {
            totalReviews: (statsData.totalReviews || 0) + 1,
            ratingDistribution,
            averageRating: averageRating,
            lastUpdated: serverTimestamp()
          });
        } else {
          // 如果统计文档不存在，创建新的
          transaction.set(statsRef, {
            totalReviews: 1,
            averageRating: reviewData.rating,
            ratingDistribution: { [reviewData.rating]: 1 },
            lastUpdated: serverTimestamp()
          });
        }
      });
      
      // 刷新评价列表和统计
      setPage(1);
      await Promise.all([loadReviews(), loadStats()]);
      
      return { success: true };
    } catch (error) {
      console.error('提交评价失败:', error);
      return {
        success: false,
        error: error.message || '提交评价失败，请稍后重试'
      };
    }
  };

  // 处理页码变化
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: document.getElementById('reviews-section').offsetTop - 100,
      behavior: 'smooth'
    });
  };

  // 处理排序变化
  const handleSortChange = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  // 渲染评分分布
  const renderRatingDistribution = () => {
    const totalReviews = stats.totalReviews || 0;
    
    return (
      <Box sx={{ mb: 4 }}>
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.ratingDistribution[rating] || 0;
          const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
          
          return (
            <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ width: '40px' }}>
                {rating}星
              </Typography>
              <Box sx={{ flex: 1, mx: 1 }}>
                <RatingBar variant="determinate" value={percentage} />
              </Box>
              <Typography variant="body2" sx={{ width: '50px', textAlign: 'right' }}>
                {count}条
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // 渲染评价日期
  const formatReviewDate = (date) => {
    if (!date) return '未知日期';
    
    try {
      return format(new Date(date), 'yyyy年MM月dd日', { locale: zhCN });
    } catch (error) {
      return '日期格式错误';
    }
  };

  // 渲染评价项
  const renderReviewItem = (review) => {
    return (
      <ReviewContainer key={review.id}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {review.userAvatar ? (
            <Box
              component="img"
              src={review.userAvatar}
              alt={review.userName}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                mr: 2,
                objectFit: 'cover',
              }}
            />
          ) : (
            <ReviewAvatar>
              {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
            </ReviewAvatar>
          )}
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">
              {review.userName || '匿名用户'}
            </Typography>
            <Rating
              value={review.rating}
              precision={0.5}
              readOnly
              size="small"
            />
          </Box>
          
          <Typography variant="caption" color="text.secondary">
            {formatReviewDate(review.createdAt)}
            {review.orderId && (
              <Chip 
                size="small" 
                label="已购买" 
                color="success" 
                variant="outlined" 
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        
        <Typography variant="body1" paragraph>
          {review.content}
        </Typography>
        
        {/* 评价图片 */}
        {review.photos && review.photos.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap' }}>
            {review.photos.map((photo, index) => (
              <ReviewImage
                key={index}
                src={photo}
                alt={`评价图片 ${index + 1}`}
                onClick={() => setSelectedImage(photo)}
              />
            ))}
          </Box>
        )}
        
        {/* 商家回复 */}
        {review.replies && review.replies.length > 0 && (
          <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="subtitle2" color="primary">
              商家回复:
            </Typography>
            <Typography variant="body2">
              {review.replies[review.replies.length - 1].content}
            </Typography>
          </Box>
        )}
      </ReviewContainer>
    );
  };

  return (
    <Box id="reviews-section">
      <Typography variant="h5" gutterBottom>
        用户评价 ({stats.totalReviews || 0})
      </Typography>
      
      {/* 评价统计 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          商品评价
        </Typography>
        
        {statsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {statsError}
          </Alert>
        ) : statsLoading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" height={100} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ textAlign: 'center', mr: 4 }}>
                <Typography variant="h3" color="primary">
                  {stats.averageRating.toFixed(1)}
                </Typography>
                <Rating
                  value={stats.averageRating}
                  precision={0.1}
                  readOnly
                  size="large"
                />
                <Typography variant="body2" color="text.secondary">
                  {stats.totalReviews} 条评价
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {renderRatingDistribution()}
              </Box>
            </Box>
          </>
        )}
      </Paper>
      
      {/* 评价表单 */}
      <ProductReviewForm onSubmit={handleReviewSubmit} />
      
      {/* 评价列表 */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>排序方式</InputLabel>
            <Select
              value={sort}
              onChange={handleSortChange}
              label="排序方式"
            >
              <MenuItem value="latest">最新</MenuItem>
              <MenuItem value="rating_high">评分从高到低</MenuItem>
              <MenuItem value="rating_low">评分从低到高</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {loading ? (
          <Box sx={{ mb: 2 }}>
            {[1, 2, 3].map((n) => (
              <Skeleton key={n} variant="rectangular" height={100} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : reviews.length === 0 ? (
          <Alert severity="info">
            暂无评价
          </Alert>
        ) : (
          <>
            {reviews.map((review) => renderReviewItem(review))}
            
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>
      
      {/* 图片预览弹窗 */}
      {selectedImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 2,
          }}
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="预览图片"
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductReviews;